import React, { useRef, useState, useCallback, useEffect } from 'react'

import { Search, Gem } from 'lucide-react'
import { useNavigate } from 'react-router-dom'

import { Pagination } from '@/core/api/dtos/common/metaPagination'
import { Playlist } from '@/core/api/entities/playlist/playlist'
import { useTranslation } from '@/core/i18n/useTranslation'
import { Button } from '@/shared/components/atoms/Button'
import Icon from '@/shared/components/atoms/Icon'
import { Input } from '@/shared/components/atoms/Input'
import { NavButton } from '@/shared/components/atoms/NavButton'
import { Skeleton } from '@/shared/components/atoms/Skeleton'
import { ActionMenuItem } from '@/shared/components/molecules/ActionDropdownMenu'
import { PageLayout } from '@/shared/components/organisms/Layout/PageLayout/PageLayout'
import { useNotificationContext } from '@/shared/context/NotificationProvider'
import { useCurrentAccount } from '@/shared/hooks/useCurrentAccount'
import { cn } from '@/shared/utils/helpers'

import FilteredPlaylistSection from './components/FilteredPlaylistSection'
import PlaylistCard from './components/PlaylistCard'
import PlaylistCarousel, {
	PlaylistCarouselRef,
} from './components/PlaylistCarousel'
import PlaylistSkeleton from './components/PlaylistSkeleton'
import ProfileIncompleteBanner from './components/ProfileIncompleteBanner'
import RequestCustomPlaylistModal from './components/RequestCustomPlaylistModal'
import { FilteredSection } from './hooks/useFilteredPlaylistSections'

// Types
interface MusicPageUIProps {
	isInitialLoading?: boolean
	searchQuery: string
	onSearchChange: (query: string) => void
	inUsePlaylists: Playlist[]
	assignedPlaylists: Playlist[]
	favoritePlaylists: Playlist[]
	filteredSections: FilteredSection[]
	hasMoreFilteredSections: boolean
	onLoadMoreFilteredSections: () => void
	searchPlaylists: Playlist[]
	isLoadingInUse: boolean
	isLoadingAssigned: boolean
	isLoadingFavorites: boolean
	isLoadingSearch: boolean
	showSearchResults: boolean
	onFavoriteClick?: (playlist: Playlist, isFavoritesCarousel: boolean) => void
	loadingPlaylistId?: number | null
	onRequestCustomPlaylistClick?: () => void
	isRequestModalOpen?: boolean
	onCloseRequestModal?: () => void
	accountType?: string
	profileIncomplete?: boolean
	inUsePagination?: Pagination
	assignedPagination?: Pagination
	favoritesPagination?: Pagination
	onInUsePageChange?: (direction: 'next' | 'prev') => void
	onAssignedPageChange?: (direction: 'next' | 'prev') => void
	onFavoritesPageChange?: (direction: 'next' | 'prev') => void
	favoritePlaylistIds?: Set<number>
	accountId?: number
	getDropdownOptions?: (playlist: Playlist) => ActionMenuItem[]
}

interface ScrollState {
	canScrollLeft: boolean
	canScrollRight: boolean
}

interface CarouselSectionProps {
	title: string
	titleIcon?: React.ReactNode
	playlists: Playlist[]
	isLoading: boolean
	carouselRef: React.RefObject<PlaylistCarouselRef | null>
	scrollState: ScrollState
	onScrollStateChange: (canScrollLeft: boolean, canScrollRight: boolean) => void
	onFavoriteClick?: (playlist: Playlist, isFavoritesCarousel: boolean) => void
	getDropdownOptions?: (playlist: Playlist) => ActionMenuItem[]
	showLocationIcon?: boolean
	showFavoriteIcon?: boolean
	skeletonCount?: number
	loadingPlaylistId?: number | null
	isFavoritesCarousel?: boolean
	onPageChange?: (direction: 'next' | 'prev') => void
	pagination?: Pagination
	playlistType: 'assigned' | 'favorites' | 'all' | 'in-use'
	favoritePlaylistIds?: Set<number>
	isCustomPlaylist?: boolean
}

interface CarouselNavigationProps {
	carouselRef: React.RefObject<PlaylistCarouselRef | null>
	scrollState: ScrollState
	onPageChange?: (direction: 'next' | 'prev') => void
	pagination?: Pagination
}

// Constants
const DEFAULT_SKELETON_COUNT = 5
const SEARCH_SKELETON_COUNT = 25

// Helper Components
const CarouselNavigationButtons: React.FC<CarouselNavigationProps> = ({
	carouselRef,
	scrollState,
	onPageChange,
	pagination,
}) => {
	const handleScrollRight = useCallback(() => {
		if (onPageChange && pagination) {
			// Use server-side pagination if available - wrap around if more than 1 page
			if (pagination.total_pages > 1) {
				onPageChange('next')
			}
		} else {
			// Fallback to client-side scrolling
			carouselRef.current?.scrollRight()
		}
	}, [carouselRef, onPageChange, pagination])

	const handleScrollLeft = useCallback(() => {
		if (onPageChange && pagination) {
			// Use server-side pagination if available - wrap around if more than 1 page
			if (pagination.total_pages > 1) {
				onPageChange('prev')
			}
		} else {
			// Fallback to client-side scrolling
			carouselRef.current?.scrollLeft()
		}
	}, [carouselRef, onPageChange, pagination])

	// Disable buttons if only 1 page or no pagination
	const canGoLeft = pagination
		? pagination.total_pages > 1
		: scrollState.canScrollLeft
	const canGoRight = pagination
		? pagination.total_pages > 1
		: scrollState.canScrollRight

	return (
		<div className="flex items-center gap-2">
			<NavButton onClick={handleScrollLeft} disabled={!canGoLeft}>
				<Icon name="chevron-left" width="16px" height="16px" color="white" />
			</NavButton>
			<NavButton onClick={handleScrollRight} disabled={!canGoRight}>
				<Icon name="chevron-right" width="16px" height="16px" color="white" />
			</NavButton>
		</div>
	)
}

const CarouselSection: React.FC<CarouselSectionProps> = ({
	title,
	titleIcon,
	playlists,
	isLoading,
	carouselRef,
	scrollState,
	onScrollStateChange,
	onFavoriteClick,
	getDropdownOptions,
	showLocationIcon = false,
	showFavoriteIcon = true,
	skeletonCount = DEFAULT_SKELETON_COUNT,
	loadingPlaylistId,
	isFavoritesCarousel = false,
	onPageChange,
	pagination,
	playlistType,
	favoritePlaylistIds,
	isCustomPlaylist = false,
}) => {
	const { t } = useTranslation('common')
	const navigate = useNavigate()

	const handleViewAllClick = () => {
		navigate(`/music/${playlistType}`)
	}

	// Don't hide section if loading - show skeleton instead
	if (playlists.length === 0 && !isLoading) {
		return null
	}

	return (
		<div className="mb-8">
			<div className="flex items-center justify-between mb-6">
				<h2 className="flex items-baseline gap-4 m-0 text-foreground text-2xl font-normal leading-[130%]">
					{titleIcon}
					{title}
					<button
						onClick={handleViewAllClick}
						className="flex items-end bg-transparent border-none opacity-80 text-xs font-medium cursor-pointer p-0 m-0 underline transition-opacity duration-200 hover:opacity-100 hover:text-foreground"
					>
						{t('entities.music.view_all')}
					</button>
				</h2>
				{/* not on mobile */}
				<div className="hidden md:block">
					<CarouselNavigationButtons
						carouselRef={carouselRef}
						scrollState={scrollState}
						onPageChange={onPageChange}
						pagination={pagination}
					/>
				</div>
			</div>
			{isLoading ? (
				<PlaylistSkeleton count={skeletonCount} />
			) : (
				<PlaylistCarousel
					ref={carouselRef}
					playlists={playlists}
					getDropdownOptions={getDropdownOptions}
					onFavoriteClick={
						onFavoriteClick
							? (playlist) => onFavoriteClick(playlist, isFavoritesCarousel)
							: undefined
					}
					showLocationIcon={showLocationIcon}
					showFavoriteIcon={showFavoriteIcon}
					isLoading={isLoading}
					onScrollStateChange={onScrollStateChange}
					loadingPlaylistId={loadingPlaylistId}
					isFavoritesCarousel={isFavoritesCarousel}
					favoritePlaylistIds={favoritePlaylistIds}
					isCustomPlaylist={isCustomPlaylist}
				/>
			)}
		</div>
	)
}

// Search Results Section
interface SearchResultsSectionProps {
	searchPlaylists: Playlist[]
	isLoadingSearch: boolean
	onFavoriteClick?: (playlist: Playlist, isFavoritesCarousel: boolean) => void
	getDropdownOptions?: (playlist: Playlist) => ActionMenuItem[]
	loadingPlaylistId?: number | null
	favoritePlaylistIds?: Set<number>
}

const SearchResultsSection: React.FC<SearchResultsSectionProps> = ({
	searchPlaylists,
	isLoadingSearch,
	onFavoriteClick,
	getDropdownOptions,
	loadingPlaylistId,
	favoritePlaylistIds,
}) => {
	const { t } = useTranslation('common')

	return (
		<div className="mb-8">
			<div className="flex items-center justify-between mb-6">
				<h2 className="flex items-baseline gap-4 m-0 text-foreground text-2xl font-semibold leading-[130%]">
					{t('entities.music.search_results')}
				</h2>
			</div>
			{isLoadingSearch ? (
				<div className="grid grid-cols-[repeat(auto-fill,minmax(200px,1fr))] gap-4 mb-8 max-md:grid-cols-[repeat(auto-fill,minmax(150px,1fr))] max-md:gap-4">
					{Array.from({ length: SEARCH_SKELETON_COUNT }).map((_, index) => (
						<PlaylistSkeleton key={index} count={1} />
					))}
				</div>
			) : searchPlaylists.length === 0 ? (
				<div className="flex flex-col items-center justify-center py-8 px-4 min-h-[300px]">
					<div className="flex flex-col items-center justify-center text-center gap-4 max-w-[400px]">
						<div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-2">
							<Icon
								name="search"
								width="32px"
								height="32px"
								color="var(--background)"
							/>
						</div>
						<h3 className="text-lg font-medium text-foreground m-0">
							{t('entities.music.no_playlists')}
						</h3>
						<p className="text-base text-muted-foreground m-0">
							{t('entities.music.search_no_results_description')}
						</p>
					</div>
				</div>
			) : (
				<div className="grid grid-cols-[repeat(auto-fill,minmax(200px,1fr))] gap-4 mb-8 max-md:grid-cols-[repeat(auto-fill,minmax(150px,1fr))] max-md:gap-4">
					{searchPlaylists.map((playlist) => {
						const isFavorite = favoritePlaylistIds
							? favoritePlaylistIds.has(playlist.id)
							: false
						return (
							<PlaylistCard
								key={playlist.id}
								playlist={playlist}
								dropdownOptions={
									getDropdownOptions ? getDropdownOptions(playlist) : undefined
								}
								onFavoriteClick={
									onFavoriteClick
										? (playlist) => onFavoriteClick(playlist, false)
										: undefined
								}
								showFavoriteIcon={true}
								isLoadingFavorite={loadingPlaylistId === playlist.id}
								isFavoritesCarousel={false}
								isFavorite={isFavorite}
							/>
						)
					})}
				</div>
			)}
		</div>
	)
}

// Main Component
const MusicPageUI: React.FC<MusicPageUIProps> = ({
	isInitialLoading = false,
	searchQuery,
	onSearchChange,
	inUsePlaylists,
	assignedPlaylists,
	favoritePlaylists,
	filteredSections,
	hasMoreFilteredSections,
	onLoadMoreFilteredSections,
	searchPlaylists,
	isLoadingInUse,
	isLoadingAssigned,
	isLoadingFavorites,
	isLoadingSearch,
	showSearchResults,
	onFavoriteClick,
	loadingPlaylistId,
	onRequestCustomPlaylistClick,
	isRequestModalOpen = false,
	onCloseRequestModal,
	profileIncomplete,
	inUsePagination,
	assignedPagination,
	favoritesPagination,
	onInUsePageChange,
	onAssignedPageChange,
	onFavoritesPageChange,
	favoritePlaylistIds,
	getDropdownOptions,
}) => {
	const { t } = useTranslation('common')
	const observerTarget = useRef<HTMLDivElement>(null)
	const navigate = useNavigate()

	// Track loading states of filtered sections
	const [loadingSectionsCount, setLoadingSectionsCount] = useState(0)
	const loadingSectionsRef = useRef<Set<string>>(new Set())

	// State for ProfileIncompleteBanner modal
	const [isProfileBannerOpen, setIsProfileBannerOpen] = useState(false)

	// Get account data for subscription/plan info
	const account = useCurrentAccount()
	const { showNotification } = useNotificationContext()

	const canRequestCustomPlaylist =
		(account?.subscriptions?.filter(
			(subscription) => subscription.plan?.metadata?.canRequestPlaylists
		).length || 0) > 0

	const handleRequestClick = () => {
		if (profileIncomplete) {
			// Show ProfileIncompleteBanner modal instead of RequestCustomPlaylistModal
			setIsProfileBannerOpen(true)
		} else {
			// Show RequestCustomPlaylistModal if profile is complete
			if (onRequestCustomPlaylistClick) {
				onRequestCustomPlaylistClick()
			}
		}
	}

	const handleUpgradeClick = () => {
		const trialSubscriptions = account?.getTrialSubscriptions() || []
		const isTrial = trialSubscriptions.length > 0

		if (isTrial) {
			navigate('/subscribe')
		} else {
			const hasBillingData = account?.billingInfo?.hasBillingData?.()
			if (!hasBillingData) {
				showNotification({
					type: 'error',
					message: t('account.upgrade_modal.error_title', {
						defaultValue: 'Cannot modify subscription',
					}),
					description: t('account.upgrade_modal.no_billing_data', {
						defaultValue:
							'You must complete your billing information before modifying your subscription. Please add your billing details first.',
					}),
				})
				return
			}

			// Avoid auto-triggering upgrade flows; take user to the subscription page.
			navigate('/account?tab=subscription&action=upgrade')
		}
	}

	// Carousel refs
	const inUseCarouselRef = useRef<PlaylistCarouselRef>(null)
	const assignedCarouselRef = useRef<PlaylistCarouselRef>(null)
	const favoritesCarouselRef = useRef<PlaylistCarouselRef>(null)

	// Scroll states
	const [inUseScrollState, setInUseScrollState] = useState<ScrollState>({
		canScrollLeft: false,
		canScrollRight: true,
	})
	const [assignedScrollState, setAssignedScrollState] = useState<ScrollState>({
		canScrollLeft: false,
		canScrollRight: true,
	})
	const [favoritesScrollState, setFavoritesScrollState] = useState<ScrollState>(
		{
			canScrollLeft: false,
			canScrollRight: true,
		}
	)

	// Handle loading state changes from filtered sections
	const handleSectionLoadingChange = useCallback(
		(sectionId: string, isLoading: boolean) => {
			if (isLoading) {
				loadingSectionsRef.current.add(sectionId)
			} else {
				loadingSectionsRef.current.delete(sectionId)
			}
			setLoadingSectionsCount(loadingSectionsRef.current.size)
		},
		[]
	)

	// Scroll state change handlers
	const handleInUseScrollStateChange = useCallback(
		(canScrollLeft: boolean, canScrollRight: boolean) => {
			setInUseScrollState({ canScrollLeft, canScrollRight })
		},
		[]
	)

	const handleAssignedScrollStateChange = useCallback(
		(canScrollLeft: boolean, canScrollRight: boolean) => {
			setAssignedScrollState({ canScrollLeft, canScrollRight })
		},
		[]
	)

	const handleFavoritesScrollStateChange = useCallback(
		(canScrollLeft: boolean, canScrollRight: boolean) => {
			setFavoritesScrollState({ canScrollLeft, canScrollRight })
		},
		[]
	)

	// Infinite scroll observer for filtered sections
	useEffect(() => {
		if (showSearchResults || !hasMoreFilteredSections) {
			return
		}

		let observer: IntersectionObserver | null = null
		let currentTarget: HTMLDivElement | null = null

		const timeoutId = setTimeout(() => {
			observer = new IntersectionObserver(
				(entries) => {
					const entry = entries[0]
					// Only load more if less than 3 sections are currently loading
					if (
						entry.isIntersecting &&
						hasMoreFilteredSections &&
						loadingSectionsCount < 3
					) {
						onLoadMoreFilteredSections()
					}
				},
				{
					threshold: 0,
					rootMargin: '200px', // Trigger 200px before reaching the bottom
				}
			)

			currentTarget = observerTarget.current
			if (currentTarget) {
				observer.observe(currentTarget)
			}
		}, 100)

		return () => {
			clearTimeout(timeoutId)
			if (observer && currentTarget) {
				observer.unobserve(currentTarget)
			}
		}
	}, [
		hasMoreFilteredSections,
		onLoadMoreFilteredSections,
		showSearchResults,
		loadingSectionsCount,
	])

	if (isInitialLoading) {
		return (
			<PageLayout
				containerClassName="md:border md:rounded-xl overflow-hidden md:bg-card"
				topAppBarLeftContent={
					<h1 className="md:hidden text-2xl font-semibold tracking-tight">
						{t('navigation.music')}
					</h1>
				}
				customHeader={
					<div className="hidden md:flex flex-col lg:flex-row w-full items-stretch lg:items-center gap-4 justify-between">
						<div className="space-y-0">
							<h2 className="flex items-center text-2xl font-normal tracking-tight md:text-3xl">
								{t('navigation.music')}
							</h2>
						</div>
					</div>
				}
			>
				<div className="mb-8 md:hidden space-y-4">
					<Skeleton className="h-10 w-full rounded-full" />
					<Skeleton className="h-10 w-full rounded-full" />
				</div>

				<div className="mb-8">
					<Skeleton className="h-5 lg:h-6 w-32 lg:w-40 mb-4" />
					<PlaylistSkeleton count={DEFAULT_SKELETON_COUNT} />
				</div>

				<div className="mb-8">
					<Skeleton className="h-5 lg:h-6 w-32 lg:w-40 mb-4" />
					<PlaylistSkeleton count={DEFAULT_SKELETON_COUNT} />
				</div>
			</PageLayout>
		)
	}

	return (
		<PageLayout
			containerClassName="md:border md:rounded-xl overflow-hidden md:bg-card"
			topAppBarLeftContent={
				<h1 className="md:hidden text-2xl font-semibold tracking-tight">
					{t('navigation.music')}
				</h1>
			}
			customHeader={
				<div className="hidden md:flex items-center justify-between">
					<div className="space-y-0">
						<h2 className="flex items-center text-2xl font-normal tracking-tight md:text-3xl">
							{t('navigation.music')}
						</h2>
					</div>
				</div>
			}
		>
			<div className="space-y-6">
				<div className="flex flex-col md:flex-row md:items-center justify-between gap-8 lg:h-8">
					<div className="w-full relative flex-1 md:max-w-md h-full">
						<Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
						<Input
							type="search"
							placeholder={t('actions.search')}
							value={searchQuery}
							onChange={(e) => onSearchChange(e.target.value)}
							className="pl-9"
						/>
					</div>
					{canRequestCustomPlaylist ? (
						<Button variant="action" size="action" onClick={handleRequestClick}>
							{t('entities.music.request_custom_playlist')}
							<Icon
								name="music-note-beamed"
								width="16px"
								height="16px"
								color="var(--background)"
							/>
						</Button>
					) : (
						<Button
							variant="action"
							size="action"
							className="rounded-full border-dashed"
							onClick={handleUpgradeClick}
						>
							<Gem className="w-4 h-4" />

							<span className="mr-2">
								{t('entities.music.request_custom_playlist')}
							</span>
						</Button>
					)}
				</div>

				{showSearchResults ? (
					<SearchResultsSection
						searchPlaylists={searchPlaylists}
						isLoadingSearch={isLoadingSearch}
						onFavoriteClick={onFavoriteClick}
						getDropdownOptions={getDropdownOptions}
						loadingPlaylistId={loadingPlaylistId}
						favoritePlaylistIds={favoritePlaylistIds}
					/>
				) : (
					<>
						{(inUsePlaylists.length > 0 || isLoadingInUse) && (
							<CarouselSection
								title={t('entities.music.assigned_playlists')}
								playlists={inUsePlaylists}
								isLoading={isLoadingInUse}
								carouselRef={inUseCarouselRef}
								scrollState={inUseScrollState}
								onScrollStateChange={handleInUseScrollStateChange}
								getDropdownOptions={getDropdownOptions}
								onFavoriteClick={onFavoriteClick}
								showFavoriteIcon={true}
								loadingPlaylistId={loadingPlaylistId}
								onPageChange={onInUsePageChange}
								pagination={inUsePagination}
								playlistType="in-use"
								favoritePlaylistIds={favoritePlaylistIds}
								isCustomPlaylist={true}
							/>
						)}

						{(assignedPlaylists.length > 0 || isLoadingAssigned) && (
							<CarouselSection
								title={t('entities.music.custom_playlists')}
								playlists={assignedPlaylists}
								isLoading={isLoadingAssigned}
								carouselRef={assignedCarouselRef}
								scrollState={assignedScrollState}
								onScrollStateChange={handleAssignedScrollStateChange}
								getDropdownOptions={getDropdownOptions}
								onFavoriteClick={onFavoriteClick}
								showFavoriteIcon={true}
								loadingPlaylistId={loadingPlaylistId}
								onPageChange={onAssignedPageChange}
								pagination={assignedPagination}
								playlistType="assigned"
								favoritePlaylistIds={favoritePlaylistIds}
								isCustomPlaylist={true}
							/>
						)}

						{(favoritePlaylists.length > 0 || isLoadingFavorites) && (
							<CarouselSection
								title={t('entities.music.favorites')}
								playlists={favoritePlaylists}
								isLoading={isLoadingFavorites}
								carouselRef={favoritesCarouselRef}
								scrollState={favoritesScrollState}
								onScrollStateChange={handleFavoritesScrollStateChange}
								getDropdownOptions={getDropdownOptions}
								onFavoriteClick={onFavoriteClick}
								showLocationIcon={true}
								showFavoriteIcon={true}
								loadingPlaylistId={loadingPlaylistId}
								isFavoritesCarousel={true}
								onPageChange={onFavoritesPageChange}
								pagination={favoritesPagination}
								playlistType="favorites"
								favoritePlaylistIds={favoritePlaylistIds}
							/>
						)}

						{filteredSections.map((section) => (
							<FilteredPlaylistSection
								key={section.id}
								section={section}
								onFavoriteClick={onFavoriteClick}
								loadingPlaylistId={loadingPlaylistId}
								favoritePlaylistIds={favoritePlaylistIds}
								onLoadingStateChange={(isLoading) =>
									handleSectionLoadingChange(section.id, isLoading)
								}
								getDropdownOptions={getDropdownOptions}
							/>
						))}

						{hasMoreFilteredSections && (
							<div ref={observerTarget} className="w-full min-h-[100px]">
								<div style={{ minHeight: '100px', width: '100%' }} />
							</div>
						)}

						{loadingSectionsCount > 0 && (
							<div
								className={cn(
									'fixed bottom-8 left-1/2 -translate-x-1/2 z-[1000]',
									'bg-card border border-border rounded-lg',
									'px-4 py-2 shadow-[0_4px_12px_rgba(0,0,0,0.15)]',
									'flex items-center justify-center backdrop-blur-[10px]',
									'animate-in slide-in-from-bottom-5 fade-in duration-300'
								)}
							>
								<div className="flex items-center gap-2">
									<div className="w-4 h-4 border-2 border-[rgba(var(--foreground),0.3)] border-t-foreground rounded-full animate-spin" />
									<span className="text-foreground text-sm font-medium">
										{t('actions.loading')}
									</span>
								</div>
							</div>
						)}
					</>
				)}
			</div>
			<ProfileIncompleteBanner
				isOpen={isProfileBannerOpen}
				onClose={() => setIsProfileBannerOpen(false)}
			/>
			<RequestCustomPlaylistModal
				isOpen={isRequestModalOpen}
				onClose={onCloseRequestModal || (() => {})}
			/>
		</PageLayout>
	)
}

export default MusicPageUI
